import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  CpuChipIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'

import IntelligenceDashboard from '../../components/intelligence/IntelligenceDashboard'
import IntelligenceSettings from '../../components/intelligence/IntelligenceSettings'
import ResponseTemplates from '../../components/intelligence/ResponseTemplates'

type TabType = 'dashboard' | 'settings' | 'templates'

const IntelligencePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: ChartBarIcon },
    { id: 'settings', name: 'Settings', icon: Cog6ToothIcon },
    { id: 'templates', name: 'Response Templates', icon: DocumentTextIcon },
  ] as const

  const handleTabSwitch = (tab: TabType) => {
    setActiveTab(tab)
    // Scroll to top when switching tabs
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <IntelligenceDashboard
            onSwitchToSettings={() => handleTabSwitch('settings')}
            onSwitchToTemplates={() => handleTabSwitch('templates')}
          />
        )
      case 'settings':
        return <IntelligenceSettings />
      case 'templates':
        return <ResponseTemplates />
      default:
        return (
          <IntelligenceDashboard
            onSwitchToSettings={() => handleTabSwitch('settings')}
            onSwitchToTemplates={() => handleTabSwitch('templates')}
          />
        )
    }
  }

  return (
    <>

      <div className="min-h-screen bg-gray-50">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center space-x-4">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <CpuChipIcon className="w-8 h-8 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Intelligence Layer</h1>
                  <p className="text-gray-600 mt-1">
                    AI-powered behavior simulation and content safety for authentic Reddit engagement
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Navigation Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6"
            >
              <nav className="flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  const isActive = activeTab === tab.id
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabSwitch(tab.id as TabType)}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        isActive
                          ? 'text-indigo-600 border-indigo-600'
                          : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  )
                })}
              </nav>
            </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default IntelligencePage